import { ArrowLeft, Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { INBOX_BODY_SHOWN_MAX, type InboxMailDetail, type InboxWhy, type UnassignedMail } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { SkeletonLines } from '../../../../components/Skeleton';
import { ServerErrorState } from '../../../../components/system-states/SystemState';
import type { ApiError } from '../../../../lib/api-client';
import { formatDateTime, formatNumber } from '../../../../lib/format';
import styles from './MailReader.module.css';

/** "Why it waits here", in words, from what the server worked out when the email was opened. */
export function whyItWaits(why: InboxWhy): string {
  const thread = why.thread === 'not_a_reply' ? 'it isn’t a reply to one of our emails' : 'it replies to an email we have no record of';
  const people = why.peopleWithAddress;
  const sender =
    people === null
      ? 'the sender’s address didn’t match exactly one person'
      : people === 0
        ? 'no one has the sender’s address'
        : people === 1
          ? 'one person has the sender’s address now (probably added after it came), so it can be filed on them'
          : `${formatNumber(people)} people share the sender’s address, so it can’t tell them apart`;
  return `Why it waits here: ${thread}, and ${sender}.`;
}

/**
 * Inbound mail's middle pane (Option A): the whole email — enough to decide whose it is — and why nothing matched.
 * The time is when it was sent, if the email said; otherwise when it was read in, labelled so.
 */
export function MailReader({
  mail,
  detail,
  error,
  onRetry,
  onBack,
}: {
  /** The list's copy, shown at once while the whole email loads. */
  readonly mail: UnassignedMail;
  readonly detail: InboxMailDetail | undefined;
  readonly error: ApiError | null;
  readonly onRetry: () => void;
  /** On a phone, the way back to the list. */
  readonly onBack?: () => void;
}): ReactNode {
  const sentAt = detail?.sentAt ?? mail.sentAt;
  return (
    <section className={styles.pane} aria-label={`Email: ${mail.subject}`}>
      <header className={styles.head}>
        {onBack && (
          <Button variant="ghost" size="sm" icon={ArrowLeft} onClick={onBack}>
            Waiting to be filed
          </Button>
        )}
        <h2 className={styles.title}>{mail.subject}</h2>
        <span className={styles.sub}>
          From {mail.from} · {sentAt ? `Sent ${formatDateTime(sentAt)}` : `Received ${formatDateTime(mail.receivedAt)}`}
        </span>
      </header>
      <div className={styles.body}>
        {detail ? (
          detail.bodyText === '' ? (
            <p className={styles.quiet}>This email has no text.</p>
          ) : (
            <p className={styles.text}>{detail.bodyText}</p>
          )
        ) : error ? (
          <ServerErrorState requestId={error.requestId} onRetry={onRetry} />
        ) : (
          <>
            {mail.excerpt !== '' && <p className={styles.text}>{mail.excerpt}</p>}
            <SkeletonLines count={4} />
          </>
        )}
        {detail?.isBodyCut && <p className={styles.quiet}>A long email: the first {formatNumber(INBOX_BODY_SHOWN_MAX)} characters are kept and shown here.</p>}
      </div>
      {detail && (
        <p className={styles.why}>
          <Info size={16} aria-hidden />
          <span>{whyItWaits(detail.why)}</span>
        </p>
      )}
    </section>
  );
}
